import { ChainId, WRAPPED_GAS_TOKENS } from '@src/configs/chainInfo'
import { TRON_PROD_WALLET } from '@src/configs/publickeys'
import { getTronGasFeeMetrics } from '@src/hooks/tron/useGasFeeMetrics'
import { ContractWriteParams } from '@src/types/gasFees'
import { GasFeeSimulationResults } from '@src/types/gasFees'
import { PrecomputeGasResults, TransactionType } from '@src/types/gasPrices'
import { getChecksumAddress } from '@src/utils/address'
import { APPROVE_SIGNATURE } from '@src/utils/functionSignatures'
import { createContextLogger } from '@src/utils/logger'
import { simulateGasFees } from '@src/utils/simulateGasFees'

const PRINT_GAS_ERRORS = false
const logger = createContextLogger('usePrecomputedTronGasPrices')

export const usePrecomputedTronGasPrices = (swapContracts: Record<string, any> | undefined) => {
    const computeTronGasPrices = async (): Promise<PrecomputeGasResults> => {
        const results: PrecomputeGasResults = {}

        try {
            const gasFeeMetrics = await getTronGasFeeMetrics()
            results[ChainId.Tron] = {
                feeMetrics: gasFeeMetrics,
                results: {
                    [TransactionType.WRAP]: {} as GasFeeSimulationResults,
                    [TransactionType.APPROVE]: {} as GasFeeSimulationResults,
                    [TransactionType.PLACE_ORDER]: {} as GasFeeSimulationResults,
                    [TransactionType.TRANSFER]: {} as GasFeeSimulationResults,
                    [TransactionType.DEPOSIT_FOR_BURN]: {} as GasFeeSimulationResults,
                },
            }

            // Simulate transactions specific to Tron
            try {
                const dummy_guy = getChecksumAddress('0xDEAD'.padEnd(42, 'f'))
                const approve_tx: ContractWriteParams = {
                    chainId: ChainId.Tron,
                    contractAddress: WRAPPED_GAS_TOKENS[ChainId.Tron].address,
                    functionSignature: APPROVE_SIGNATURE,
                    functionParams: [
                        { type: 'address', value: dummy_guy },
                        {
                            type: 'uint256',
                            value: '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff',
                        },
                    ],
                    value: BigInt(0),
                    caller: TRON_PROD_WALLET,
                }
                const approve_fee = await simulateGasFees([approve_tx], gasFeeMetrics)
                results[ChainId.Tron].results[TransactionType.APPROVE] = approve_fee
            } catch (e) {
                PRINT_GAS_ERRORS && logger.error('Unable to simulate Tron approve transaction:', e)
            }
        } catch (err) {
            PRINT_GAS_ERRORS && logger.error('Error computing Tron gas prices:', err)
        }

        return results
    }

    return { computeTronGasPrices }
}
